import { createContext, type ReactNode, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Translation } from './translations';
import { convertPath } from './routes';
import { de } from './translations/de';
import { en } from './translations/en';
import { it } from './translations/it';
import { es } from './translations/es';
import { fr } from './translations/fr';

export type SupportedLang = 'en' | 'de' | 'it' | 'es' | 'fr';

export const SUPPORTED_LANGS: SupportedLang[] = ['en', 'de', 'it', 'es', 'fr'];

export const LANG_LABELS: Record<SupportedLang, string> = {
  en: 'English',
  de: 'Deutsch',
  it: 'Italiano',
  es: 'Espa\u00f1ol',
  fr: 'Fran\u00e7ais',
};

export interface LanguageContextValue {
  lang: SupportedLang;
  t: Translation;
  switchLang: () => void;
  switchTo: (lang: SupportedLang) => void;
}

export const LanguageContext = createContext<LanguageContextValue | null>(null);

const translations: Record<SupportedLang, Translation> = { en, de, it, es, fr };

function detectLang(pathname: string): SupportedLang {
  const segment = pathname.split('/')[1];
  if (SUPPORTED_LANGS.includes(segment as SupportedLang)) return segment as SupportedLang;
  return 'en';
}

function stripLangPrefix(pathname: string): string {
  const match = pathname.match(/^\/(en|de|it|es|fr)(\/.*)?$/);
  return match ? (match[2] || '/') : pathname;
}

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = detectLang(location.pathname);

  const value = useMemo<LanguageContextValue>(
    () => ({
      lang,
      t: translations[lang],
      switchTo: (targetLang: SupportedLang) => {
        if (targetLang === lang) return;
        const suffix = stripLangPrefix(location.pathname);
        const converted = convertPath(suffix, lang, targetLang);
        navigate(`/${targetLang}${converted === '/' ? '' : converted}`);
      },
      switchLang: () => {
        // Legacy toggle: cycle to next language
        const idx = SUPPORTED_LANGS.indexOf(lang);
        const next = SUPPORTED_LANGS[(idx + 1) % SUPPORTED_LANGS.length];
        const suffix = stripLangPrefix(location.pathname);
        const converted = convertPath(suffix, lang, next);
        navigate(`/${next}${converted === '/' ? '' : converted}`);
      },
    }),
    [lang, location.pathname, navigate],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
